import { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IHomeTileOption } from "@/api_framework/api_modals/HomeTileConfig";
import { RibbonPreview, TileAvatar } from "../components";
import {
  cardClassName,
  IHomeTileConfigFormFields,
  RIBBON_POSITION_CLASSNAME,
  RIBBON_POSITION_LABEL,
  TILE_TYPE_LABEL,
  isFullWidthRibbon,
} from "../interface";

const SummaryRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className="text-[11px] text-slate-500">{label}</span>
    <span className="truncate text-[11px] font-semibold text-slate-700">{value}</span>
  </div>
);

export const PreviewCard = ({ tile }: { tile?: IHomeTileOption }) => {
  const { control } = useFormContext<IHomeTileConfigFormFields>();
  const [ribbonText = "", ribbon, status] = useWatch({ control, name: ["ribbon_text", "ribbon", "status"] });
  const isActive = status === "ACTIVE";

  return (
    <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[380px]">
      <div className={cardClassName}>
        <div className="border-b border-slate-100 px-5 py-3">
          <div className="text-sm font-semibold text-gray-900">Preview</div>
          <p className="mt-0.5 text-[11px] text-gray-500">How the tile reads on the app home screen.</p>
        </div>

        <div className="flex justify-center bg-slate-50 px-6 py-10">
          <div className="flex w-[240px] flex-col items-center gap-3">
            <div className="relative w-full">
              <div className="flex h-[200px] w-full items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-[#F7F7F8] shadow-md">
                <TileAvatar
                  name={tile?.tile_name ?? "?"}
                  imageUrl={tile?.image_url}
                  className="h-[168px] w-[200px] rounded-2xl bg-transparent p-2 text-4xl"
                />
              </div>
              {ribbonText.trim() && (
                <div className={`absolute z-10 max-w-full ${RIBBON_POSITION_CLASSNAME[ribbon.position]}`}>
                  <RibbonPreview
                    text={ribbonText}
                    style={ribbon}
                    isActive={isActive}
                    isFullWidth={isFullWidthRibbon(ribbon.position)}
                    size="lg"
                  />
                </div>
              )}
            </div>
            <span className="text-center text-xl font-semibold text-slate-800">{tile?.tile_name ?? "Tile"}</span>
          </div>
        </div>

        <div className="divide-y divide-slate-100 border-t border-slate-100 px-5 py-2">
          <SummaryRow label="Tile" value={tile?.tile_name ?? "—"} />
          <SummaryRow label="Tile Type" value={tile ? TILE_TYPE_LABEL[tile.tile_type] : "—"} />
          <SummaryRow label="Position" value={RIBBON_POSITION_LABEL[ribbon.position]} />
          <SummaryRow label="Status" value={isActive ? "Active" : "InActive"} />
        </div>
      </div>
    </aside>
  );
};
